/**
 * Builds the staged event payload for a reviewed Triage item.
 *
 * A Triage review never writes directly: it produces an in-memory event that the
 * normal Event modal opens, so the reviewer still passes the usual save gate
 * before anything is persisted. This module owns that translation — reviewed
 * defects, note, Next Advise, and ERTS onto either a fresh record (CREATE) or a
 * loaded one (UPDATE).
 *
 * Kept beside TriageSection rather than in `src/utils/triage-*` because it
 * depends on the event service and the modal's decision type; the `src/utils`
 * triage helpers are deliberately pure.
 */
import { EVENT_STATUS } from '../constants';
import { UnscheduledEvent } from '../types';
import { TriageItem } from '../services/triageService';
import type { TriageErtsAction } from '../services/triageService';
import { fetchEventById } from '../services/eventService';
import { createLogger } from '../utils/logger';
import { processNoteText } from '../utils/processNoteText';
import {
  defectComparisonKey,
  isCompoundDefectCovered,
  isDuplicateDefect,
} from '../utils/triage-defects';
import { noteIndicatesCompletion } from '../utils/triage-completion';
import { createEmptyEvent } from './eventModal/createEmptyEvent';
import { TriageDecision } from './TriageReviewModal';

const logger = createLogger('TriageEventBuilder');

/**
 * Combines a date (YYYY-MM-DD) and Zulu time (HH:MM) into the canonical event
 * field format (e.g. 2026-06-24 18:00:00). Returns the date alone when no time
 * is given, or '' when no date is given.
 */
function combineZulu(date: string, time: string): string {
  if (!date) return '';
  const trimmed: string = time.trim();
  return trimmed ? `${date} ${trimmed}:00` : date;
}

/** Combines extracted declared values in the format EventModal controls use. */
function combineDeclaredDateTime(date?: string, time?: string): string {
  if (!date) return '';
  const trimmedTime: string = time?.trim() || '';
  return trimmedTime ? `${date} ${trimmedTime}:00` : date;
}

/** Returns the initial status required for a new record type. */
function createStatus(recordType: string): string {
  return recordType === 'Unscheduled' ? EVENT_STATUS.IN_WORK : EVENT_STATUS.PENDING;
}

/** Builds a single note entry from the reviewer's note text (empty -> none). */
function buildNoteEntries(
  note: string,
  date: string,
  time: string,
  currentUserAlias: string,
): UnscheduledEvent['noteEntries'] {
  if (!note) return [];
  const now = new Date();
  return [
    {
      id: `note-${now.getTime()}`,
      date: date || now.toISOString().slice(0, 10),
      time: time || '',
      alias: currentUserAlias || '—',
      defectIds: [],
      text: processNoteText(note),
    },
  ];
}

/** Normalizes reviewed defect text and removes duplicate candidate defects. */
function normalizeDefectTexts(defectTexts: string[]): string[] {
  const seenTexts: Set<string> = new Set<string>();
  return defectTexts.reduce<string[]>((normalizedTexts: string[], defectText: string): string[] => {
    const text: string = processNoteText(defectText).trim();
    const comparisonText: string = defectComparisonKey(text);
    if (!text || seenTexts.has(comparisonText)) return normalizedTexts;
    seenTexts.add(comparisonText);
    return [...normalizedTexts, text];
  }, []);
}

/**
 * Builds a lean new-event payload from a triage item for a CREATE commit. The
 * backend stamps ids/timestamps/version and denormalizes the rest. The declared
 * time, Note, Next Advise, and ERTS fields seed the corresponding event fields.
 */
function buildStarterEvent(
  item: TriageItem,
  decision: TriageDecision,
  currentUserAlias: string,
): UnscheduledEvent {
  const defects = normalizeDefectTexts(decision.parsedDefects).map(
    (text: string, index: number) => ({ id: index + 1, text }),
  );
  return {
    ...createEmptyEvent(),
    recordType: decision.targetRecordType,
    status: createStatus(decision.targetRecordType),
    timeDeclaredZulu: combineDeclaredDateTime(item.declaredDate, item.declaredTimeZulu),
    tailNumber: item.tailNumber || '',
    carrier: item.carrier,
    aircraftType: item.aircraftType,
    defects,
    noteEntries: buildNoteEntries(
      decision.note,
      decision.noteDate,
      decision.noteTime,
      currentUserAlias,
    ),
    nextAdviseTimeZulu: combineZulu(decision.nextAdviseDate, decision.nextAdviseTime),
    returnToServiceTimeZulu:
      decision.ertsAction === 'set' ? combineZulu(decision.ertsDate, decision.ertsTime) : '',
  };
}

/** Resolves a reviewer's explicit ERTS intent without inferring removals from blanks. */
function applyTriageErtsAction(base: UnscheduledEvent, decision: TriageDecision): string {
  switch (decision.ertsAction) {
    case 'none':
      return base.returnToServiceTimeZulu;
    case 'set':
      return combineZulu(decision.ertsDate, decision.ertsTime);
    case 'remove':
      return '';
    default: {
      const exhaustiveAction: never = decision.ertsAction;
      return exhaustiveAction;
    }
  }
}

/** Normalizes event timestamps to the Date/Time precision shown in Triage. */
function normalizeErtsForComparison(value: string): string {
  const normalizedValue: string = value.trim().replace('T', ' ').replace(/Z$/i, '');
  const match: RegExpMatchArray | null = normalizedValue.match(
    /^(\d{4}-\d{2}-\d{2})(?:\s+(\d{2}):(\d{2})(?::\d{2})?)?$/,
  );
  if (!match) return normalizedValue;
  return match[2] && match[3] ? `${match[1]} ${match[2]}:${match[3]}` : match[1];
}

/** Avoids creating an ERTS revision when Triage exactly matches the current value. */
function resolveTriageErtsAction(
  base: UnscheduledEvent,
  decision: TriageDecision,
): TriageErtsAction {
  switch (decision.ertsAction) {
    case 'none':
    case 'remove':
      return decision.ertsAction;
    case 'set': {
      const reviewedErts: string = normalizeErtsForComparison(
        combineZulu(decision.ertsDate, decision.ertsTime),
      );
      const existingErts: string = normalizeErtsForComparison(base.returnToServiceTimeZulu);
      return reviewedErts === existingErts ? 'none' : 'set';
    }
    default: {
      const exhaustiveAction: never = decision.ertsAction;
      return exhaustiveAction;
    }
  }
}

/** Removes all source-note data when the ingest record explicitly suppresses it. */
export function suppressSourceNote(item: TriageItem, decision: TriageDecision): TriageDecision {
  if (!item.noteSuppressed) return decision;
  return {
    ...decision,
    note: '',
    noteDate: '',
    noteTime: '',
  };
}

/** Returns every valid defect identifier carried by an event. */
function getDefectIds(event: UnscheduledEvent): number[] {
  return (event.defects || []).map((defect: { id: number; text: string }): number => defect.id);
}

/** Returns defects appended to a Triage update, excluding existing event rows. */
function getAppendedDefectIds(base: UnscheduledEvent, updated: UnscheduledEvent): number[] {
  const baseDefectIds: Set<number> = new Set<number>(getDefectIds(base));
  return getDefectIds(updated).filter((defectId: number): boolean => !baseDefectIds.has(defectId));
}

/**
 * Merges reviewed defects, Note, Next Advise, and ERTS onto a loaded event.
 * Existing defect IDs and their linked data remain unchanged; appended defects
 * use IDs above every current ID so they can never overwrite a deleted row.
 */
function mergeUpdateEvent(
  base: UnscheduledEvent,
  decision: TriageDecision,
  currentUserAlias: string,
): UnscheduledEvent {
  const nextAdvise = combineZulu(decision.nextAdviseDate, decision.nextAdviseTime);
  const existingDefects = base.defects || [];
  const existingDefectTexts: string[] = existingDefects.map((defect): string =>
    processNoteText(defect.text),
  );
  const nextDefectId: number =
    existingDefects.reduce(
      (highestId: number, defect): number => Math.max(highestId, defect.id),
      0,
    ) + 1;
  const appendedDefects = normalizeDefectTexts(decision.parsedDefects)
    .filter(
      (text: string): boolean =>
        !isDuplicateDefect(text, existingDefectTexts) &&
        !isCompoundDefectCovered(text, existingDefectTexts),
    )
    .map((text: string, index: number) => ({ id: nextDefectId + index, text }));
  const defects = [...existingDefects, ...appendedDefects];
  return {
    ...base,
    defects,
    nextAdviseTimeZulu: nextAdvise || base.nextAdviseTimeZulu,
    returnToServiceTimeZulu: applyTriageErtsAction(base, decision),
    noteEntries: [
      ...(base.noteEntries || []),
      ...(buildNoteEntries(decision.note, decision.noteDate, decision.noteTime, currentUserAlias) ||
        []),
    ],
  };
}

/** The staged event plus the ERTS bookkeeping the Event modal needs. */
export interface PreparedTriageEvent {
  event: UnscheduledEvent;
  ertsAction: TriageErtsAction;
  ertsExistedBefore: boolean;
  recommendationDefectIds: number[];
}

/** Loads and merges the UPDATE target, or null when it can't be resolved. */
async function prepareUpdateEvent(
  decision: TriageDecision,
  targetEventId: string,
  currentUserAlias: string,
): Promise<PreparedTriageEvent | null> {
  let full: UnscheduledEvent | null;
  try {
    full = await fetchEventById(targetEventId);
  } catch (error: unknown) {
    const message: string = error instanceof Error ? error.message : 'Unknown error';
    logger.error('Triage UPDATE target could not be loaded', {
      eventId: targetEventId,
      error: message,
    });
    return null;
  }
  if (!full) {
    logger.warn('Triage UPDATE target was not found', { eventId: targetEventId });
    return null;
  }
  const ertsAction: TriageErtsAction = resolveTriageErtsAction(full, decision);
  const event: UnscheduledEvent = mergeUpdateEvent(
    full,
    { ...decision, ertsAction },
    currentUserAlias,
  );
  return {
    event,
    ertsAction,
    ertsExistedBefore: Boolean(full.returnToServiceTimeZulu || full.ertsHistory?.length),
    recommendationDefectIds: getAppendedDefectIds(full, event),
  };
}

/**
 * Applies a note that reports the work is finished: the record closes as
 * COMPLETED, and for non-Deferred types the note's timestamp becomes the final
 * ERTS, which supersedes any reviewed ERTS action.
 */
function applyNoteCompletion(
  prepared: PreparedTriageEvent,
  decision: TriageDecision,
): PreparedTriageEvent {
  if (!noteIndicatesCompletion(decision.note)) return prepared;
  if (prepared.event.recordType === 'Deferred') {
    return { ...prepared, event: { ...prepared.event, status: EVENT_STATUS.COMPLETED } };
  }
  return {
    ...prepared,
    event: {
      ...prepared.event,
      status: EVENT_STATUS.COMPLETED,
      returnToServiceTimeZulu: combineZulu(decision.noteDate, decision.noteTime),
    },
    ertsAction: 'none',
  };
}

/** Builds the staged event data for a reviewed Triage action. */
export async function prepareTriageEvent(
  item: TriageItem,
  decision: TriageDecision,
  currentUserAlias: string,
): Promise<PreparedTriageEvent | null> {
  let prepared: PreparedTriageEvent | null;
  if (decision.action === 'UPDATE' && decision.targetEventId) {
    try {
      prepared = await prepareUpdateEvent(decision, decision.targetEventId, currentUserAlias);
    } catch (error: unknown) {
      // prepareUpdateEvent already turns a failed load into null, so reaching here
      // means one of its synchronous merge helpers threw. Rethrow rather than
      // return null: null is the caller's signal that the UPDATE target could not
      // be resolved, and it reports that as a missing event. A merge defect is not
      // a missing event, and must not be shown to a controller as one.
      const message: string = error instanceof Error ? error.message : 'Unknown error';
      logger.error('Triage UPDATE event could not be prepared', {
        eventId: decision.targetEventId,
        error: message,
      });
      throw error;
    }
    if (!prepared) return null;
  } else {
    const event: UnscheduledEvent = buildStarterEvent(item, decision, currentUserAlias);
    prepared = {
      event,
      ertsAction: decision.ertsAction,
      ertsExistedBefore: false,
      recommendationDefectIds: getDefectIds(event),
    };
  }
  if (item.deferred) Object.assign(prepared.event, item.deferred);
  return applyNoteCompletion(prepared, decision);
}
